/**
 * Notification System v2 — shared client core (same file in care-admin, care-provider
 * and care). No React here: notifyUi.jsx renders it.
 *
 * What it does (spec §3-§4, §11.2):
 *  - Live connection to the NotificationV2 WebSocket server with automatic
 *    reconnect + backoff; on reconnect it fetches everything with an id greater
 *    than the last one received (never replays pop-ups).
 *  - Reports this tab's visibility + a heartbeat so the server can choose pop-up
 *    vs browser push (the server decides; the tab only reports).
 *  - Pop-ups: only a visible tab shows one, after winning a per-notification claim
 *    across tabs (Web Locks, BroadcastChannel fallback); the focused tab wins.
 *    It confirms what it showed. Bursts collapse into one summary; one sound per
 *    burst at most (3 s apart), never as the only signal.
 *  - Browser push: service worker registration, subscription saved per signed-in
 *    person, removed on sign-out; accurate permission status.
 *
 * Created once per signed-in session:
 *   const client = createNotifyClient({ portal: 'admin', wsUrl, getToken, request });
 *   client.start();  …  client.signOut();
 * request(method, path, body) must call the API with the user's token and resolve
 * to the parsed JSON body ({error, message, data}).
 */

const TAB_KEY = 'aman-notify-tab';
const SW_PATH = '/notify-sw.js';

function tabId() {
  try {
    let id = sessionStorage.getItem(TAB_KEY);
    if (!id) {
      id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(TAB_KEY, id);
    }
    return id;
  } catch (e) {
    return `t-${Math.random().toString(36).slice(2, 12)}`;
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** A short two-tone "ding" made with Web Audio, so no sound file is needed. */
function makeDing() {
  let ctx = null;
  const unlock = () => {
    try {
      if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) ctx = new AC();
      }
      if (ctx && ctx.state === 'suspended') ctx.resume();
    } catch (e) { /* audio unavailable */ }
  };
  const play = () => {
    try {
      if (!ctx || ctx.state !== 'running') return false; // still blocked: fail silently (§4.6)
      const now = ctx.currentTime;
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + i * 0.09);
        gain.gain.exponentialRampToValueAtTime(0.18, now + i * 0.09 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.09 + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.09);
        osc.stop(now + i * 0.09 + 0.4);
      });
      return true;
    } catch (e) {
      return false;
    }
  };
  return { unlock, play };
}

export function createNotifyClient({ portal, appCode, wsUrl, getToken, request }) {
  const listeners = new Set();
  const tab = tabId();
  const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(`aman-notify-${portal}`) : null;
  const ding = makeDing();

  let state = {
    ready: false,
    enabled: false,
    connected: false,
    unread: 0,
    preferences: { browser_push_enabled: true, sound_enabled: true },
    tuning: { collapse_threshold: 3, collapse_window_seconds: 10, sound_gap_seconds: 3, late_popup_seconds: 30, heartbeat_seconds: 20 },
    webPush: { configured: false, vapid_public_key: '' },
    pushStatus: 'unknown',
    pushError: '',
    lastId: 0,
  };
  let ws = null;
  let stopped = true;
  let attempts = 0;
  let heartbeatTimer = null;
  let reconnectTimer = null;
  let lastSound = 0;
  let burst = [];
  const claimedByOthers = new Set();

  const emit = (type, payload) => listeners.forEach((fn) => { try { fn(type, payload, state); } catch (e) { /* ignore */ } });
  const setState = (patch) => { state = { ...state, ...patch }; emit('state', state); };

  // ---------------------------------------------------------------- API
  const api = {
    config: () => request('get', 'v2/notifications-v2/centre/config'),
    page: (params) => request('get', 'v2/notifications-v2/centre', params),
    since: (afterId) => request('get', 'v2/notifications-v2/centre/since', { after_id: afterId }),
    unread: () => request('get', 'v2/notifications-v2/centre/unread-count'),
    read: (body) => request('post', 'v2/notifications-v2/centre/read', body),
    remove: (body) => request('post', 'v2/notifications-v2/centre/delete', body),
    open: (id) => request('post', `v2/notifications-v2/centre/${id}/open`, {}),
    ack: (id) => request('post', `v2/notifications-v2/centre/${id}/popup-ack`, {}),
    prefs: (body) => request('put', 'v2/notifications-v2/centre/preferences', body),
    subscribe: (body) => request('post', 'v2/notifications-v2/web-push/subscription', body),
    unsubscribe: (body) => request('delete', 'v2/notifications-v2/web-push/subscription', body),
  };

  const visible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';
  const focused = () => typeof document !== 'undefined' && document.hasFocus();

  // ------------------------------------------------------- presence (§4.2)
  function sendPresence() {
    if (ws && ws.readyState === 1) {
      ws.send(JSON.stringify({ type: 'presence', tab_id: tab, portal, visible: visible(), focused: focused() }));
    }
  }
  const onVisibility = () => {
    sendPresence();
    if (visible()) refreshUnread();
  };
  const onFocus = () => { sendPresence(); if (bc) bc.postMessage({ type: 'focus', tab, at: Date.now() }); };
  const onFirstGesture = () => ding.unlock();

  // ---------------------------------------------- cross-tab claim (§4.4)
  if (bc) {
    bc.onmessage = (e) => {
      const m = e.data || {};
      if (m.type === 'claim' && m.tab !== tab) claimedByOthers.add(m.id);
      if (m.type === 'sync') emit('sync', m.payload);
    };
  }

  /** Win the right to show notification `id` in this tab (first claim wins, focused tab first). */
  async function claim(id) {
    if (!visible()) return false;
    if (!focused()) await sleep(200);
    if (!visible()) return false;
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      return new Promise((resolve) => {
        navigator.locks.request(`aman-notify-${portal}-${id}`, { ifAvailable: true }, (lock) => {
          if (!lock) { resolve(false); return undefined; }
          resolve(true);
          return sleep(60000); // hold it so a tab that becomes leader mid-handover still skips
        }).catch(() => resolve(false));
      });
    }
    if (claimedByOthers.has(id)) return false;
    if (bc) bc.postMessage({ type: 'claim', id, tab });
    await sleep(80);
    return !claimedByOthers.has(id);
  }

  // ---------------------------------------------------- pop-ups (§4.5)
  function ackPopup(id) {
    if (ws && ws.readyState === 1) ws.send(JSON.stringify({ type: 'popup_ack', id }));
    else api.ack(id).catch(() => {});
  }

  function maybeDing() {
    if (!state.preferences.sound_enabled) return;
    const now = Date.now();
    if (now - lastSound < state.tuning.sound_gap_seconds * 1000) return;
    if (ding.play()) lastSound = now;
  }

  async function handlePopup(item) {
    const t = state.tuning;
    // Late pop-up: too old → centre and count only (§4.3).
    if (!item.created_unix || Date.now() / 1000 - item.created_unix > t.late_popup_seconds) return;
    if (!(await claim(item.id))) return;
    // A hidden tab never shows or confirms (§4.3).
    if (!visible()) return;
    ackPopup(item.id);

    const now = Date.now();
    burst = burst.filter((b) => now - b.at < t.collapse_window_seconds * 1000);
    burst.push({ at: now, item });
    if (burst.length >= t.collapse_threshold) {
      // Replace the individual pop-ups of this burst with one summary, grouped by type.
      const groups = {};
      burst.forEach(({ item: it }) => {
        groups[it.event_type] = groups[it.event_type] || { event_type: it.event_type, title: it.title, count: 0, critical: false };
        groups[it.event_type].count += 1;
        groups[it.event_type].critical = groups[it.event_type].critical || it.severity === 'critical';
      });
      emit('summary', { ids: burst.map((b) => b.item.id), groups: Object.values(groups) });
    } else {
      emit('popup', item);
    }
    maybeDing();
  }

  // ------------------------------------------------- live connection (§4.8)
  function handleMessage(raw) {
    let m;
    try { m = JSON.parse(raw); } catch (e) { return; }
    if (!m || !m.type) return;
    if (m.type === 'authenticated') {
      attempts = 0;
      setState({ connected: true });
      sendPresence();
      recover();
      return;
    }
    if (m.type === 'error' && m.code === 'AUTH_FAILED') {
      stop();
      return;
    }
    if (m.type === 'notify.new' && m.notification) {
      const item = m.notification;
      setState({ unread: m.unread_count ?? state.unread, lastId: Math.max(state.lastId, item.id) });
      emit('new', item);
      if (m.popup) handlePopup(item);
      return;
    }
    if (m.type === 'notify.sync') {
      setState({ unread: m.unread_count ?? state.unread });
      emit('sync', m);
      return;
    }
    if (m.type === 'notify.preferences' && m.preferences) {
      setState({ preferences: m.preferences });
      return;
    }
    // Live badge counters: an absolute snapshot for this user's audience (all
    // staff, or one facility). Consumers replace, never add (useCounters hooks).
    if (m.type === 'counters.sync' && m.counters) {
      emit('counters', m);
    }
  }

  function connect() {
    if (stopped || !wsUrl) return;
    const token = getToken();
    if (!token) return;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      scheduleReconnect();
      return;
    }
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: `Bearer ${token}`, app: portal, app_code: appCode }));
    ws.onmessage = (e) => handleMessage(e.data);
    ws.onclose = () => {
      setState({ connected: false });
      scheduleReconnect();
    };
    ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
  }

  function scheduleReconnect() {
    if (stopped) return;
    clearTimeout(reconnectTimer);
    attempts += 1;
    const delay = Math.min(30000, 1000 * 2 ** Math.min(attempts, 5)) * (0.75 + Math.random() * 0.5);
    reconnectTimer = setTimeout(connect, delay);
  }

  /** After a (re)connect: fetch what was missed, never as pop-ups (§4.9). */
  async function recover() {
    try {
      const res = await api.since(state.lastId);
      const items = res?.data?.items || [];
      items.forEach((it) => emit('new', it));
      const maxId = items.reduce((m, it) => Math.max(m, it.id), state.lastId);
      setState({ unread: res?.data?.unread_count ?? state.unread, lastId: maxId });
    } catch (e) { /* the centre still loads on demand */ }
  }

  async function refreshUnread() {
    try {
      const res = await api.unread();
      if (res && !res.error) setState({ unread: res.data.unread_count });
    } catch (e) { /* ignore */ }
  }

  // ------------------------------------------------- browser push (§4.7)
  function pushSupported() {
    return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  async function computePushStatus() {
    if (!pushSupported()) return 'unsupported';
    if (!state.webPush.configured) return 'not_configured';
    if (Notification.permission === 'denied') return 'blocked';
    if (Notification.permission === 'default') return 'not_asked';
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      return sub ? 'subscribed' : 'granted';
    } catch (e) {
      return 'granted';
    }
  }

  async function refreshPushStatus() {
    setState({ pushStatus: await computePushStatus() });
  }

  /** The server key a subscription was created with, base64url, or '' when unknown. */
  function subscriptionKey(sub) {
    try {
      const key = sub && sub.options && sub.options.applicationServerKey;
      if (!key) return '';
      const bytes = new Uint8Array(key);
      let bin = '';
      bytes.forEach((b) => { bin += String.fromCharCode(b); });
      return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) {
      return '';
    }
  }

  async function ensureSubscription() {
    if (!pushSupported() || !state.webPush.configured || Notification.permission !== 'granted') return;
    try {
      const reg = await navigator.serviceWorker.register(SW_PATH);
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      const serverKey = String(state.webPush.vapid_public_key || '').replace(/=+$/, '');
      if (sub && serverKey && subscriptionKey(sub) && subscriptionKey(sub) !== serverKey) {
        // The server's VAPID key changed: pushes signed with the new key would be
        // rejected for this subscription, so replace it.
        try { await api.unsubscribe({ endpoint: sub.endpoint }); } catch (e) { /* ignore */ }
        try { await sub.unsubscribe(); } catch (e) { /* ignore */ }
        sub = null;
      }
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(state.webPush.vapid_public_key),
        });
      }
      // Saving it also moves this browser to the signed-in person (§4.7).
      const res = await api.subscribe({ subscription: sub.toJSON(), content_encoding: (PushManager.supportedContentEncodings || ['aes128gcm'])[0] });
      if (res && res.error) throw new Error(res.message || 'The server did not accept this browser\'s subscription.');
      setState({ pushError: '' });
    } catch (e) {
      setState({ pushError: (e && e.message) || 'This browser could not be registered for notifications.' });
      throw e;
    }
  }

  /**
   * Shows a notification from this browser's service worker, without the server.
   * Proves permission, the service worker and the OS notification setting together.
   */
  async function testPush() {
    if (!pushSupported()) return { ok: false, message: 'This browser does not support notifications.' };
    if (Notification.permission !== 'granted') return { ok: false, message: 'Notifications are not allowed in this browser yet.' };
    try {
      const reg = (await navigator.serviceWorker.getRegistration(SW_PATH)) || (await navigator.serviceWorker.register(SW_PATH));
      await navigator.serviceWorker.ready;
      await reg.showNotification('Aman HMO notifications are working', {
        body: 'You will be notified here when this portal is not open in a visible tab.',
        tag: 'aman-notify-test',
        icon: '/favicon.ico',
        data: { path: '/notifications' },
      });
      return { ok: true, message: 'A test notification was shown. If you did not see it, check the operating system\'s notification settings (Focus / Do Not Disturb) for this browser.' };
    } catch (e) {
      return { ok: false, message: (e && e.message) || 'The browser refused to show a notification.' };
    }
  }

  /** Call from a user gesture (a toggle or button), never on page load (§4.7). */
  async function enablePush() {
    if (!state.preferences.browser_push_enabled) await updatePreferences({ browser_push_enabled: true });
    if (!pushSupported()) { await refreshPushStatus(); return state.pushStatus; }
    if (Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch (e) { /* ignore */ }
    }
    try { await ensureSubscription(); } catch (e) { /* status shows the outcome */ }
    await refreshPushStatus();
    return state.pushStatus;
  }

  async function removeSubscription() {
    if (!pushSupported()) return;
    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.unsubscribe({ endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
    } catch (e) { /* ignore */ }
  }

  async function updatePreferences(changes) {
    const res = await api.prefs(changes);
    if (res && !res.error) setState({ preferences: res.data.preferences });
    return state.preferences;
  }

  // ------------------------------------------------------------ lifecycle
  async function start() {
    if (!stopped) return;
    stopped = false;
    try {
      const res = await api.config();
      if (!res || res.error) { setState({ ready: true }); return; }
      const d = res.data;
      setState({
        ready: true, enabled: !!d.enabled, preferences: d.preferences, tuning: { ...state.tuning, ...d.tuning }, webPush: d.web_push, categories: d.categories || [],
      });
      if (!d.enabled) return;
    } catch (e) {
      setState({ ready: true });
      return;
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onFocus);
      window.addEventListener('blur', sendPresence);
      ['click', 'keydown', 'touchstart'].forEach((ev) => window.addEventListener(ev, onFirstGesture, { once: false, passive: true }));
    }
    heartbeatTimer = setInterval(sendPresence, Math.max(10, state.tuning.heartbeat_seconds) * 1000);
    refreshUnread();
    connect();
    refreshPushStatus().then(() => {
      // Refresh the subscription on every sign-in when permission is already granted.
      if (state.preferences.browser_push_enabled && state.pushStatus !== 'blocked') ensureSubscription().then(refreshPushStatus).catch(() => {});
    });
    if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'notifications' }).then((p) => { p.onchange = () => refreshPushStatus(); }).catch(() => {});
    }
  }

  function stop() {
    stopped = true;
    clearTimeout(reconnectTimer);
    clearInterval(heartbeatTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', sendPresence);
      ['click', 'keydown', 'touchstart'].forEach((ev) => window.removeEventListener(ev, onFirstGesture));
    }
    if (ws) { try { ws.onclose = null; ws.close(); } catch (e) { /* ignore */ } }
    ws = null;
    setState({ connected: false });
  }

  /** Sign-out: stop, and make sure this browser stops receiving this person's pushes (§4.7). */
  async function signOut() {
    await removeSubscription();
    stop();
  }

  return {
    start,
    stop,
    signOut,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    getState: () => state,
    api,
    async markRead(ids) {
      const res = await api.read(ids === 'all' ? { all: true } : { ids });
      if (res && !res.error) setState({ unread: res.data.unread_count });
      return res;
    },
    async remove(ids) {
      const res = await api.remove(ids === 'all' ? { all: true } : { ids });
      if (res && !res.error) setState({ unread: res.data.unread_count });
      return res;
    },
    async open(id) {
      const res = await api.open(id);
      refreshUnread();
      if (!res || res.error) {
        // A failed request is not "no access": say what happened so it can be retried.
        return { allowed: false, message: res?.message || 'The notification could not be opened. Please try again.' };
      }
      return res.data || { allowed: false, message: 'You no longer have access to this item.' };
    },
    testPush,
    updatePreferences,
    enablePush,
    refreshPushStatus,
    unlockSound: ding.unlock,
  };
}
